import { useEffect, useRef, useState } from 'react'
import { Loader2, Send, Sparkles, Trash2 } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ApiClientError, apiClient } from '@/lib/api/client'
import type { HelmReplyDraft } from '@/lib/api/types'
import { useAsyncData } from '@/src/hooks/useAsyncData'

const POLL_MS = 2_000
const POLL_LIMIT_MS = 120_000

/** Business time is Kampala time, wherever the reader is. */
function kampala(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Africa/Kampala',
  })
}

function findingsFrom(error: unknown): string[] | null {
  if (!(error instanceof ApiClientError) || error.code !== 'REPLY_DRAFT_HAS_FINDINGS') return null
  const details = error.details as { findings?: unknown } | undefined
  return Array.isArray(details?.findings) ? details.findings.map(String) : [error.message]
}

function DraftEditor({
  draft,
  onDone,
}: {
  draft: HelmReplyDraft
  onDone: (sent: boolean) => void
}) {
  const [subject, setSubject] = useState(draft.subject ?? '')
  const [body, setBody] = useState(draft.body)
  const [findings, setFindings] = useState<string[]>(draft.findings)
  const [busy, setBusy] = useState<'send' | 'discard' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const edited = subject !== (draft.subject ?? '') || body !== draft.body

  async function send() {
    setBusy('send')
    setError(null)
    try {
      await apiClient.sendReplyDraft(draft.id, {
        subject: subject.trim() || null,
        body: body.trim(),
      })
      onDone(true)
    } catch (caught) {
      const found = findingsFrom(caught)
      if (found) setFindings(found)
      else setError(caught instanceof Error ? caught.message : 'The reply was not sent')
    } finally {
      setBusy(null)
    }
  }

  async function discard() {
    setBusy('discard')
    setError(null)
    try {
      await apiClient.discardReplyDraft(draft.id)
      onDone(false)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The draft was not discarded')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="space-y-4 rounded-md border p-4" data-testid="reply-draft">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-sm">
        <span className="font-medium">Reply to {draft.recipient ?? 'the client'}</span>
        <span className="text-xs text-muted-foreground">
          {draft.requested_by ? 'Drafted on request' : 'Drafted by Beacon'} ·{' '}
          {kampala(draft.created_at)}
        </span>
      </div>

      {draft.replying_to && (
        <blockquote className="space-y-1 rounded-md bg-muted px-3 py-2 text-sm">
          <div className="text-xs text-muted-foreground">
            {draft.replying_to.from ?? 'The client'} wrote {kampala(draft.replying_to.at)}
          </div>
          <p className="line-clamp-4 whitespace-pre-wrap">{draft.replying_to.excerpt}</p>
        </blockquote>
      )}

      {findings.length > 0 && (
        <Alert variant="destructive">
          <AlertTitle>Edit these out before sending</AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-4">
              {findings.map((finding) => (
                <li key={finding}>{finding}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {draft.needs_from_team.length > 0 && (
        <Alert>
          <AlertTitle>The draft could not answer</AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-4">
              {draft.needs_from_team.map((need) => (
                <li key={need}>{need}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor={`draft-subject-${draft.id}`}>Subject</Label>
        <Input
          id={`draft-subject-${draft.id}`}
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          disabled={busy !== null}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`draft-body-${draft.id}`}>Message</Label>
        <Textarea
          id={`draft-body-${draft.id}`}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          disabled={busy !== null}
          rows={9}
        />
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => void send()} disabled={busy !== null || !body.trim()}>
          {busy === 'send' ? <Loader2 className="animate-spin" /> : <Send />}
          {edited ? 'Send edited reply' : 'Send reply'}
        </Button>
        <Button variant="ghost" onClick={() => void discard()} disabled={busy !== null}>
          {busy === 'discard' ? <Loader2 className="animate-spin" /> : <Trash2 />}
          Discard
        </Button>
        <span className="text-xs text-muted-foreground">
          Sent from Helm into the client&apos;s thread, as any update is.
        </span>
      </div>
    </div>
  )
}

/**
 * Replies the assistant drafted for this inquiry, waiting for a person. They
 * can be edited, sent or discarded here; nothing reaches the client otherwise.
 */
export function ReplyDrafts({ inquiryId, onSent }: { inquiryId: string; onSent: () => void }) {
  const drafts = useAsyncData(() => apiClient.listReplyDrafts(inquiryId), [inquiryId])
  const [drafting, setDrafting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Stops following a draft job once the page is gone. Set on every mount:
  // React may unmount and mount again without a new component.
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  async function requestDraft() {
    setDrafting(true)
    setError(null)
    try {
      const { job_id } = await apiClient.requestReplyDraft(inquiryId)
      const started = Date.now()
      // Drafting runs in the background; follow the job until it settles.
      while (mounted.current && Date.now() - started < POLL_LIMIT_MS) {
        await new Promise((resolve) => setTimeout(resolve, POLL_MS))
        const job = await apiClient.getJob(job_id)
        if (job.status === 'SUCCEEDED') break
        if (job.status === 'FAILED' || job.status === 'CANCELLED') {
          throw new Error(job.last_error || 'The draft could not be written')
        }
      }
      drafts.reload()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The draft could not be written')
    } finally {
      if (mounted.current) setDrafting(false)
    }
  }

  const open = drafts.data ?? []

  return (
    <section className="space-y-3" aria-labelledby="reply-drafts-heading">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id="reply-drafts-heading" className="text-sm font-medium">
          Reply drafts
        </h3>
        <Button variant="outline" size="sm" onClick={() => void requestDraft()} disabled={drafting}>
          {drafting ? <Loader2 className="animate-spin" /> : <Sparkles />}
          {drafting ? 'Drafting…' : open.length ? 'Draft again' : 'Draft a reply'}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {drafts.error && !drafts.data ? (
        <p className="text-sm text-muted-foreground">Couldn&apos;t load drafts.</p>
      ) : open.length === 0 ? (
        <p className="rounded-md border p-4 text-sm text-muted-foreground">
          No reply is waiting. When the client writes, Beacon drafts one for you to check; you can
          also ask for one.
        </p>
      ) : (
        open.map((draft) => (
          <DraftEditor
            key={draft.id}
            draft={draft}
            onDone={(sent) => {
              drafts.reload()
              if (sent) onSent()
            }}
          />
        ))
      )}
    </section>
  )
}
